import {
  Box,
  Button,
  CircularProgress,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../api/use-project";
import { useEffect, useMemo, useState } from "react";
import NewProject from "./new-project";
import { DateTime } from "luxon";
import { Link as RouterLink } from "react-router-dom";
import { InlineEditableText } from "../ui/inline-editable-text.jsx";
import { throttle } from 'lodash';
import { useMutation } from "@tanstack/react-query";
import { patchProject } from "../../api/project-api.js";
import { ProjectStateIcon } from "./project-state-icon.jsx";
import PaymentsIcon from '@mui/icons-material/Payments';
import { BuyProject } from "./buy/buy-project.jsx";

const useThrottledNoteMutation = () => {
  const mutation = useMutation({
    mutationFn: ({ project, note }) => {
      return patchProject(project.id, {
        note
      });
    },
  });

  const mutate = useMemo(
    () => throttle(mutation.mutate, 500),
    [mutation.mutate]
  );

  return { ...mutation, mutate };
};

export const Projects = () => {
  const { t } = useTranslation(["project", "common"]);
  const [dataPerPage, setDataPerPage] = useState({});
  const [page, setPage] = useState(0);
  const pageSize = 60;
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [{
    data,
    loading
  }, execute] = useProjects(pageSize, page * pageSize, searchTerm === "" ? undefined : searchTerm);
  const noteMutation = useThrottledNoteMutation();
  const [buyProject, setBuyProject] = useState(null);

  useEffect(() => {
    if (data)
      setDataPerPage((d) => ({ ...d, [page]: data }));
  }, [page, data]);

  const handleLoadMore = () => {
    if (!dataPerPage[page] || dataPerPage[page].length < pageSize) execute();
    else setPage((p) => p + 1);
  };

  const [notes, setNotes] = useState({});

  useEffect(() => {
    let entries = Object.values(dataPerPage).flatMap((data) => data.map(p => [p.id, p.note]));
    setNotes(Object.fromEntries(entries));
  }, [dataPerPage]);

  function handleUpdateNote(x, value) {
    setNotes((n) => ({ ...n, [x.id]: value }));
    noteMutation.mutate({ project: x, note: value });
  }

  const TableCellLinkWrapper = ({ p, children }) => {
    return (
      <Box
        component={RouterLink}
        to={`/projects/${p.id}`}
        sx={{
          textDecoration: "none",
          color: "text.primary",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
        }}
      >
        {children}
      </Box>
    );
  };

  function onCloseBuyProject(success) {
    setBuyProject(null);
    if (success) {
      execute();
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex" }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t("projects")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField sx={{ mr: 2, width: "300px" }}
                     size={"small"}
                     placeholder={t("search")}
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}/>
          <Zoom in={true} unmountOnExit>
            <Fab
              variant="extended"
              color="primary"
              onClick={() => setNewProjectOpen(true)}
            >
              <AddIcon sx={{ mr: 1 }}/>
              {t("newProject")}
            </Fab>
          </Zoom>
        </Box>
      </Box>
      <Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead
              sx={{
                "& th": {
                  whiteSpace: "nowrap",
                  p: 2,
                  fontWeight: 700
                },
                "& th:first-of-type": { width: "100%" },
              }}>
              <TableRow>
                <TableCell>{t("projectName")}</TableCell>
                <TableCell>{t("createdAt")}</TableCell>
                <TableCell>{t("state")}</TableCell>
                <TableCell>{t("note")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[]
                .concat(...Object.values(dataPerPage))
                .filter((p) => p)
                .map((p) => (
                  <TableRow
                    key={p.id}
                    sx={{
                      "&:hover td": { backgroundColor: "#303030" },
                      "&:last-child td, &:last-child th": { border: 0 },
                      "& td": { whiteSpace: "nowrap", p: 0 },
                      "& td:first-of-type": { width: "100%" },
                    }}
                  >
                    <TableCell>
                      <TableCellLinkWrapper p={p}>
                        {p.name}
                      </TableCellLinkWrapper>
                    </TableCell>
                    <TableCell>
                      <TableCellLinkWrapper p={p}>
                        {DateTime.fromISO(p.createdAt).toFormat("yyyy.MM.dd")}
                      </TableCellLinkWrapper>
                    </TableCell>
                    <TableCell>
                      {p.state === "finished" && !p.paid &&
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 2,
                          }}
                        >
                          <PaymentsIcon/>
                          {t(`states.waitForBuy`)}
                          <Button variant="outlined" size={"small"}
                                  onClick={() => setBuyProject(p.id)}>{t("buy")}</Button>
                        </Box>
                      }
                      {!(p.state === "finished" && !p.paid) &&
                        <TableCellLinkWrapper p={p}>
                          <ProjectStateIcon state={p.state}/>
                          {t(`states.${p.state}`)}
                        </TableCellLinkWrapper>
                      }
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        px: 2
                      }}>
                        <InlineEditableText
                          value={notes[p.id] || ""}
                          onChange={(x) => handleUpdateNote(p, x)}
                          placeHolder={t("addNote")}
                          autoComplete={"new-password"}
                          sx={{
                            width: "300px",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden"
                          }}/>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 3,
          }}
        >
          {loading && <CircularProgress size={30} sx={{ my: 3 }}/>}
          {!loading && <Button onClick={handleLoadMore}>
            {t("loadMore")}
          </Button>}
        </Box>
        <NewProject
          open={newProjectOpen}
          onClose={() => setNewProjectOpen(false)}
        />
      </Box>
      <BuyProject open={buyProject !== null} onClose={onCloseBuyProject} projectId={buyProject}/>
    </Paper>
  );
};
